// Cross-tenant cockpit API client (B5B-PARTNER-COCKPIT). The partner-cockpit-worker authenticates the
// firm consultant's real session (httpOnly cookie, credentialed CORS) and scopes every response to the
// consultant's mandate. Like the team/profile surfaces this works behind real auth; in dev (no cookie) it
// returns 401, which the routes render as a "sign in" state.
import { API_BASE } from "./env";
import { refreshSession } from "./auth";

export interface TenantKpi {
  tenantId: string; tenantName: string; engagementEndAt: string | null;
  openTickets: number; dueSoon: number; programs: number;
  score: number | null; band: "red" | "amber" | "green" | "none"; mandateEndingSoon: boolean;
}
export interface PartnerPortfolio {
  firmId: string | null; firmName: string | null;
  tenants: TenantKpi[];
  totals: { tenants: number; openTickets: number; dueSoon: number; programs: number; mandatesEndingSoon: number };
}
export type InboxTicket = {
  id: string; tenantId: string; tenantName: string; title: string;
  state: string | null; priority: string | null; dueAt: string | null; verwerkingId: string | null;
};
export interface ConsultantFirm { firmId: string; firmName: string; tenantCount: number }

export interface TenantDrillIn {
  tenantId: string; tenantName: string; readOnly: boolean;
  kpis: { openTickets: number; dueSoon: number; programs: number; verwerkingen: number };
  recentTickets: { id: string; title: string; state: string | null; dueAt: string | null }[];
  verwerkingen: { id: string; name: string; status: string }[];
}
export type SearchHit = { entity: "ticket" | "verwerking"; id: string; title: string; tenantId: string; tenantName: string; context: string | null };
export type PortfolioProgram = { id: string; tenantId: string; tenantName: string; name: string; status: string; startedAt: string | null; expectedCompletionAt: string | null };
export interface FirmSettings {
  firmId: string; legalName: string; tradeName: string | null; certified: boolean; cadenceWeeks: number | null;
  certifications: { kind: string; issuer: string | null; issuedAt: string | null; expiresAt: string | null }[];
  tenants: { engagementId: string; tenantId: string; tenantName: string; status: string; consultants: number }[];
  consultants: { ipuId: string; userId: string; email: string; fullName: string | null; role: string; tenantName: string; revoked: boolean }[];
}

export interface TicketDetail {
  id: string; tenantId: string; tenantName: string;
  title: string; body: string | null; lifecycle: string; state: string | null;
  priority: string | null; dueAt: string | null; resolvedAt: string | null;
  verwerkingId: string | null; verwerkingName: string | null;
  transitions: { id: string; fromState: string | null; toState: string; transitionedAt: string; transitionKind: string | null }[];
}
export interface VerwerkingDetail {
  id: string; tenantId: string; tenantName: string; name: string; description: string | null;
  status: string; actingOrgRole: string; heightenedRisk: boolean; incidental: boolean;
  effectiveFrom: string | null; effectiveTo: string | null;
  doelen: { id: string; name: string; status: string }[];
  openTickets: number;
}
export interface ProgramDetail {
  id: string; tenantId: string; tenantName: string; name: string; description: string | null;
  status: string; startedAt: string | null; expectedCompletionAt: string | null; completedAt: string | null;
  gates: { id: string; sprintNumber: number; sprintStageCode: string | null; decision: string; decidedAt: string | null; rationale: string | null }[];
}

export class CockpitError extends Error {
  constructor(public status: number, message: string) { super(message); this.name = "CockpitError"; }
}

async function get<T>(path: string): Promise<T> {
  const send = () => fetch(`${API_BASE.cockpit}${path}`, {
    credentials: "include",
    headers: { "content-type": "application/json" },
  });
  let res = await send();
  // Silent refresh-and-retry once on 401 (expired 15-min access token → rotate off the 14-day session).
  if (res.status === 401 && (await refreshSession())) res = await send();
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new CockpitError(res.status, body || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const cockpit = {
  firms: () => get<ConsultantFirm[]>("/firms"),
  portfolio: (firmId?: string) => get<PartnerPortfolio>(`/portfolio${firmId ? `?firmId=${encodeURIComponent(firmId)}` : ""}`),
  inbox: (opts?: { firmId?: string; tenantId?: string; status?: "open" | "all" }) => {
    const qs = new URLSearchParams();
    if (opts?.firmId) qs.set("firmId", opts.firmId);
    if (opts?.tenantId) qs.set("tenantId", opts.tenantId);
    if (opts?.status) qs.set("status", opts.status);
    const q = qs.toString();
    return get<InboxTicket[]>(`/inbox${q ? `?${q}` : ""}`);
  },
  drillIn: (tenantId: string) => get<TenantDrillIn>(`/tenant/${encodeURIComponent(tenantId)}`),
  search: (q: string) => get<SearchHit[]>(`/search?q=${encodeURIComponent(q)}`),
  programs: () => get<PortfolioProgram[]>("/programs"),
  firmSettings: (firmId: string) => get<FirmSettings>(`/firm/${encodeURIComponent(firmId)}/settings`),
  ticket: (id: string) => get<TicketDetail>(`/ticket/${encodeURIComponent(id)}`),
  verwerking: (id: string) => get<VerwerkingDetail>(`/verwerking/${encodeURIComponent(id)}`),
  program: (id: string) => get<ProgramDetail>(`/program/${encodeURIComponent(id)}`),
};
